import { useEffect, useRef } from 'react';
import npsFallbackStylesheet from './nps-fallback-stylesheet';
const KEY_LAST_NPS_LOADED = '@lastNpsLoaded';
const KEY_AUTH_NPS_TOKEN = 'kcIdToken';
const NPS_SCRIPT_ID = 'nps-script';
const NPS_STYLE_GUARD_ID = 'nps-style-guard';
/**
 * O widget monta `<div id="{sistema}-nps" data-version="…">`. É o que dá para
 * casar sem depender do nome do sistema, que vem da API.
 */
const NPS_CONTAINER_SELECTOR = '[id$="-nps"][data-version]';
const NPS_CSS_READY_ATTR = 'data-nps-css';
const NPS_INLINE_STYLE_ID = 'nps-inline-style';
/** Quanto esperar o `<link>` antes de tentar o contorno. */
const NPS_CSS_FALLBACK_MS = 2500;
/**
 * O widget do NPS injeta a própria folha de estilo em um `<link>` **dentro do
 * body**, junto do modal. Folha no body não bloqueia a pintura do que veio
 * antes dela, então existe uma janela em que o modal já está no DOM e o CSS
 * ainda não chegou — e, se o CSS nunca chegar, essa janela não fecha. Sem
 * estilo o modal não é modal: vira conteúdo em fluxo no fim da página, com
 * todos os passos visíveis de uma vez, porque `hidden` ali é classe e não o
 * atributo HTML.
 *
 * Esta guarda esconde o container até a folha dele aplicar. Se ela falhar ou
 * demorar demais, o widget é removido em vez de aparecer quebrado — a pesquisa
 * é opcional, o resto da tela não.
 */
const useLoadNps = ({ mfeStage, currentMfeState, nps, userData, tenant, userProfile, }) => {
    const guardCleanupRef = useRef(null);
    /**
     * Documento do MFE no palco. O widget se anexa ao `body` de quem o executa,
     * então é ali que ele precisa rodar — e não no documento da RootApp.
     */
    const getStageDocument = () => mfeStage?.contentWindow?.document ?? null;
    const getNpsLocalStorageValue = (keyPrefix) => {
        const key = Object.keys(localStorage).find((k) => k.startsWith(keyPrefix));
        return key ? { key, value: localStorage.getItem(key) || '' } : null;
    };
    const setOptionalVarsNps = () => {
        if (nps?.group)
            localStorage.setItem('npsGroup', nps?.group);
        if (nps?.unitCode)
            localStorage.setItem('npsUnitCode', nps?.unitCode);
    };
    const getNpsMeta = () => {
        if (!userProfile?.profile) {
            return null;
        }
        return { tenant: tenant.currentTenant, ...userProfile.profile };
    };
    const removeVarsNps = () => {
        const npsSkip = getNpsLocalStorageValue('npsSkip');
        const npsClose = getNpsLocalStorageValue('npsClose');
        const npsGroup = getNpsLocalStorageValue('npsGroup');
        const npsUnitCode = getNpsLocalStorageValue('npsUnitCode');
        if (npsSkip)
            localStorage.removeItem(npsSkip.key);
        if (npsClose)
            localStorage.removeItem(npsClose.key);
        if (npsGroup)
            localStorage.removeItem('npsGroup');
        if (npsUnitCode)
            localStorage.removeItem('npsUnitCode');
    };
    const updateNpsLocalStorage = () => {
        if (!nps)
            return;
        const storedData = localStorage.getItem(KEY_LAST_NPS_LOADED);
        if (!storedData)
            return;
        try {
            const parsedData = JSON.parse(storedData);
            const userNpsData = parsedData[nps.productId]?.[userData.userName];
            if (userNpsData?.npsSkip) {
                localStorage.setItem(userNpsData.npsSkip.key, userNpsData.npsSkip.value);
            }
            if (userNpsData?.npsClose) {
                localStorage.setItem(userNpsData.npsClose.key, userNpsData.npsClose.value);
            }
        }
        catch (error) {
            console.error('Erro ao processar localStorage:', error);
        }
    };
    const removeNpsScript = () => {
        guardCleanupRef.current?.();
        if (!nps)
            return;
        // O nome sempre prometeu isso, mas só o localStorage era limpo. Sem remover
        // a tag, o palco fica com resíduo sempre que o documento sobrevive à troca.
        getStageDocument()?.getElementById(NPS_SCRIPT_ID)?.remove();
        const npsSkip = getNpsLocalStorageValue('npsSkip');
        const npsClose = getNpsLocalStorageValue('npsClose');
        if (!npsSkip && !npsClose)
            return;
        const storedData = localStorage.getItem(KEY_LAST_NPS_LOADED);
        const parsedData = storedData
            ? JSON.parse(storedData)
            : {};
        if (!parsedData[nps.productId]) {
            parsedData[nps.productId] = {};
        }
        if (!parsedData[nps.productId][userData.userName]) {
            parsedData[nps.productId][userData.userName] = {};
        }
        if (npsSkip)
            parsedData[nps.productId][userData.userName].npsSkip = npsSkip;
        if (npsClose)
            parsedData[nps.productId][userData.userName].npsClose = npsClose;
        localStorage.setItem(KEY_LAST_NPS_LOADED, JSON.stringify(parsedData));
        removeVarsNps();
    };
    /**
     * Regra que esconde o container enquanto ele não estiver marcado como pronto.
     * Vai no documento antes do script, então vale já na primeira pintura.
     */
    const installStyleGuard = (stageDocument) => {
        if (stageDocument.getElementById(NPS_STYLE_GUARD_ID) != null)
            return;
        const style = stageDocument.createElement('style');
        style.id = NPS_STYLE_GUARD_ID;
        style.textContent =
            `${NPS_CONTAINER_SELECTOR}:not([${NPS_CSS_READY_ATTR}='ready'])` +
                '{display:none !important;}';
        (stageDocument.head ?? stageDocument.documentElement).appendChild(style);
    };
    /**
     * Contorno para a CSP do proxy reverso, cujo `style-src` libera o host de
     * *staging* do NPS e não o de produção — o `<link>` do widget é bloqueado e o
     * modal pinta sem estilo, virando conteúdo em fluxo no rodapé.
     *
     * Buscar a folha em runtime não resolve: o CDN do NPS não devolve
     * `Access-Control-Allow-Origin` nos assets estáticos, então o `fetch` é
     * barrado por CORS mesmo passando pela CSP. Como o `style-src` traz
     * `'unsafe-inline'`, a saída é aplicar a cópia versionada em
     * `nps-fallback-stylesheet.ts`.
     *
     * É contorno, não conserto — as instruções de remoção estão naquele arquivo.
     */
    const applyFallbackStylesheet = (stageDocument) => {
        if (stageDocument.getElementById(NPS_INLINE_STYLE_ID) != null)
            return;
        const style = stageDocument.createElement('style');
        style.id = NPS_INLINE_STYLE_ID;
        style.textContent = npsFallbackStylesheet;
        (stageDocument.head ?? stageDocument.documentElement).appendChild(style);
    };
    const watchNpsStylesheet = (stageDocument) => {
        const timers = [];
        let resolvido = false;
        const limparTimers = () => {
            timers.splice(0).forEach(clearTimeout);
        };
        const reveal = (container) => {
            if (resolvido)
                return;
            resolvido = true;
            limparTimers();
            container.setAttribute(NPS_CSS_READY_ATTR, 'ready');
        };
        const observer = new MutationObserver(() => {
            const container = stageDocument.querySelector(NPS_CONTAINER_SELECTOR);
            if (container == null)
                return;
            observer.disconnect();
            const link = container.querySelector('link[rel="stylesheet"]');
            // Sem folha própria não há o que esperar: o widget se vira com o que tem.
            if (link == null || link.sheet != null) {
                reveal(container);
                return;
            }
            const contornar = () => {
                if (resolvido)
                    return;
                applyFallbackStylesheet(stageDocument);
                reveal(container);
            };
            link.addEventListener('load', () => reveal(container), { once: true });
            // CSP bloqueando dispara `error`; se o browser não disparar, o timer
            // abaixo cobre o mesmo caso.
            link.addEventListener('error', contornar, { once: true });
            timers.push(setTimeout(contornar, NPS_CSS_FALLBACK_MS));
        });
        observer.observe(stageDocument.body, { childList: true, subtree: true });
        guardCleanupRef.current = () => {
            observer.disconnect();
            limparTimers();
            guardCleanupRef.current = null;
        };
    };
    const injectNpsScript = () => {
        if (!nps)
            return;
        if (!userProfile?.profile)
            return;
        const stageDocument = getStageDocument();
        if (stageDocument == null)
            return;
        // O palco é um iframe só, reaproveitado entre MFEs. Sem essa guarda, cada
        // execução do efeito empilha mais uma tag no mesmo documento.
        if (stageDocument.getElementById(NPS_SCRIPT_ID) != null)
            return;
        removeVarsNps();
        updateNpsLocalStorage();
        setOptionalVarsNps();
        installStyleGuard(stageDocument);
        watchNpsStylesheet(stageDocument);
        const iframe = stageDocument.body;
        const metaData = getNpsMeta();
        const script = stageDocument.createElement('script');
        script.id = NPS_SCRIPT_ID;
        script.src = nps.baseApi;
        script.setAttribute('host', nps.host);
        script.setAttribute('productId', nps.productId);
        script.setAttribute('auth', KEY_AUTH_NPS_TOKEN);
        script.setAttribute('storage', 'local');
        script.setAttribute('group', 'npsGroup');
        script.setAttribute('unitCode', 'npsUnitCode');
        script.setAttribute('metadata', JSON.stringify(metaData));
        script.async = true;
        script.onload = () => console.log('Script NPS carregado!');
        iframe.appendChild(script);
    };
    useEffect(() => {
        if (currentMfeState === 'LOADED' &&
            nps &&
            !nps.hide &&
            userProfile?.profile) {
            injectNpsScript();
            return removeNpsScript;
        }
        // Depende de `profile`, não da store: a store é a mesma instância a vida
        // toda, então depender dela faz a chegada do perfil passar batida.
    }, [currentMfeState, nps, userProfile?.profile]);
};
export default useLoadNps;
//# sourceMappingURL=use-load-nps.js.map